import { PromiseUtils } from './promise';
import { StatusValue } from './statusValue';
import { TransportStatus, DispatchStatus } from './train';
import lodash from 'lodash';

/**
 * :DOC: 加减单逻辑的开发设计文档:
 * http://confluence.midea.com/pages/viewpage.action?pageId=244734892
 */

/**
 * 减单验证
 * @param isPrivilege 用户是否特权用户
 * @param targetDispatch 目标发车单对象封装
 * @param targetDispatch.isGainingContract 目标发车单 是否 正在获取合同号
 * @param tasksToDelete 待删除的运单数组
 * tasksToDelete 的元素(运单任务)依赖的字段
 * - taskNo
 * - taskStatus
 * - dispatchNo
 * - inOutType
 * - logisticMode
 * @returns {void}
 */
export function validateDeletingTasks(isPrivilege, targetDispatch, tasksToDelete) {
    PromiseUtils.assert(!targetDispatch.isGainingContract, '获取合同号中，不可以加单');

    tasksToDelete.forEach(row => {
        const taskStatus = StatusValue.create(+row.taskStatus);
        // 在 "待发车" 状态
        const isToDispatch = taskStatus.is(TransportStatus.TO_DISPATCH);
        // 在 "在途" 或 在途后的状态
        const isToOrAfterDispatch = taskStatus.isOrAfter(DispatchStatus.IN_TRANSIT);
        // 有发车单号
        const hasDispatchNo = !!row.dispatchNo;
        // 是 纯运输(YS) 或 销售出库(OUT) 类型的运单
        const isOutYs = row.inOutType === 'OUT' || row.inOutType === 'YS';
        // 是仓配分离的运单
        const isWT = row.logisticMode === 'WT';

        PromiseUtils.assertNot(
            isToDispatch && hasDispatchNo && isOutYs && !isWT,
            `存在不能减单的运输单：${row.taskNo}`
        );

        PromiseUtils.assertNot(
            !isPrivilege && isWT,
            `存在不能减单的运输单：${row.taskNo}`
        );

        PromiseUtils.assertNot(
            isPrivilege && isWT && isToOrAfterDispatch,
            `仓配分离运输单：${row.taskNo} 是在途前的状态才能减单`
        );
    });
}

/**
 * 加单验证
 * @param isPrivilege 用户是否特权用户
 * @param targetDispatch 目标发车单对象封装
 * @param targetDispatch.isWarehouseSendInOne 目标发车单 是否 仓配一体
 * @param targetDispatch.isBeforeSend 目标发车单 是否 在途前状态
 * @param targetDispatch.wtWhCode 目标发车单 仓库列表
 * @param targetTasks 目标发车单的运单数组
 * @param tasksToAdd 待添加的运单数组
 * tasksToAdd 的元素(运单任务)依赖的字段
 * - logisticMode
 * - scanNum
 * - whCode
 * @param tasksMayToAdd 潜在的待添加运单数组（包含 `tasksToAdd` 的所有元素，用于 获取完整的 LBX 运单）
 * @returns {Task[]} 运单数组 (按customerOrderNo去重, 同customerOrderNo放在 children 下面，包括自身)]
 */
export function validateAddingTasks(isPrivilege, targetDispatch, targetTasks, tasksToAdd, tasksMayToAdd) {
    // 待添加运单和已添加运单的行比较验证
    validateEachTasks(tasksToAdd[0], tasksToAdd.slice(1).concat(targetTasks));

    // 整合LBX运单 并 验证
    return integrateLbxTasks(tasksToAdd, tasksMayToAdd, true);
}

/**
 * 待添加运单和已添加运单的行比较验证
 * **只需要用其他行与第一行进行比较就好了**
 * @param firstRow
 * @param restRows
 * firstRow, restRows 的元素(运单任务)依赖的字段
 * - inOutType
 * - planOrderFlag
 * - warehouseGroup
 * - finalDetailAddr
 * - deliveryType
 * - logisticMode
 */
function validateEachTasks(firstRow, restRows) {
    // 因为所有行要按本函数的规则兼容可以退出如下结论：
    // 首行是 纯运输 或 销售出库类 运单 :=> 所有行都是 纯运输 或 销售出库类 运单
    const firstIsOutYs = ['OUT', 'YS'].includes(firstRow.inOutType);

    restRows.forEach(row => {
        PromiseUtils.assert(
            row.planOrderFlag === firstRow.planOrderFlag,
            '模糊运单和非模糊运单不能同时排车');
        if (row.planOrderFlag === 1) {
            // row 是 模糊运单
            PromiseUtils.assert(
                row.inOutType === firstRow.inOutType,
                '模糊运单只支持同类型单据排车');
        } else {
            // row 是 非模糊运单
            PromiseUtils.assert(
                row.inOutType === firstRow.inOutType ||
                (firstIsOutYs && ['OUT', 'YS'].includes(row.inOutType)),
                '非模糊运单只支持 同类型 或 纯运输和销售出库类 运单排车');
        }

        if (!firstIsOutYs) {
            // (首)行 ***不是*** 纯运输 或 销售出库类 运单 时
            PromiseUtils.assert(
                row.warehouseGroup === firstRow.warehouseGroup,
                '存在不同仓库分组的运输单');
        }
    });

    // 是 销售出库单 的所有行
    const outTypeRows = [firstRow, ...restRows].filter(row => row.inOutType === 'OUT');
    if (outTypeRows.length > 2) {
        PromiseUtils.assert(
            !outTypeRows.slice(1).some(row => row.warehouseGroup !== outTypeRows[0].warehouseGroup),
            '存在不同仓库分组的销售出库单');
    }
    // 强制预约时，必须都有预约时间才能加单
    const arriveAppointmentRequireCheck = [firstRow, ...restRows].some(row => +row.arriveAppointmentRequireFlag === 2 && !row.appointmentTime);
    PromiseUtils.assertNot(
        arriveAppointmentRequireCheck,
        '存在强制预约的客户订单，需要预约派送时间');
     // 快销订单单独排
    const projectClassifyCheck = ([firstRow, ...restRows].some(item => { return item.projectClassify === 'QCO'; }) && ![firstRow, ...restRows].every(i => i.projectClassify === 'QCO'));
    PromiseUtils.assert(
        !projectClassifyCheck,
        '快销订单不允许与其他订单混排');
    // 搬仓订单单独排
    const freightBasisCheck = ([firstRow, ...restRows].some(item => { return +item.freightBasis === 40; }) && ![firstRow, ...restRows].every(i => +i.freightBasis === 40));
    PromiseUtils.assert(
        !freightBasisCheck,
        '搬仓订单与非搬仓订单不允许混排！');
}

/**
 * 整合 非LBX运单 和 LBX完整运单列表, 并验证
 * @param tasksToAdd {*[]}待添加的运单数组
 * tasksToAdd 的元素(运单任务)依赖的字段
 * - customerOrderNo
 * - isFull
 * @param tasksMayToAdd {*[]} 潜在的待添加运单数组（包含 `tasksToAdd` 的所有元素，用于 获取完整的 LBX 运单）
 * @param withValidation {boolean=false} 是否验证 LBX 运单, 默认
 * @returns {Task[]} 运单数组 (按customerOrderNo去重, 同customerOrderNo放在 children 下面，包括自身)]
 */
export function integrateLbxTasks(tasksToAdd, tasksMayToAdd = [], withValidation = false) {
    // 结果 运单 数组, 由 非LBX运单 和 带children的LBX运单 组成,
    // 其中 children 是 customerOrderNo 相同的运单
    const resultTasks = [];
    // LBX 运单 客户订单号 集合
    const lbxCustomerOrderNos = new Set();
    tasksToAdd.forEach((item) => {
        if (isLbxTask(item)) {
            if (!lbxCustomerOrderNos.has(item.customerOrderNo)) {
                lbxCustomerOrderNos.add(item.customerOrderNo);
                resultTasks.push(item);
            }
        } else {
            resultTasks.push(item);
        }
    });

    if (withValidation) {
        const isNotFullLbxCustomerOrderNos = [];
        tasksMayToAdd.forEach((mayItem) => {
            if (lbxCustomerOrderNos.has(mayItem.customerOrderNo) && !mayItem.isFull) {
                isNotFullLbxCustomerOrderNos.push(mayItem.customerOrderNo);
            }
        });
        PromiseUtils.assert(
            isNotFullLbxCustomerOrderNos.length === 0,
            `客户订单号：${isNotFullLbxCustomerOrderNos.join(',')}没有整单下发，请在OTP任务中心做下发`
        );
    }

    // 由于 customerOrderNo 相同的 LBX 运单, 必须放在同一个发车单
    //
    // 即逻辑上他们是同一个运单
    // customerOrderNo 去重的 LBX 运单, 其 children 字段存放与其 customerOrderNo 相同的 LBX 运单（包括自己）
    // { customerOrderNo: 'aaa', children: [{ customerOrderNo: 'aaa' }, ...]}
    const lbxCustomerOrderNosMap = {};
    tasksMayToAdd.forEach((mayItem) => {
        if (lbxCustomerOrderNos.has(mayItem.customerOrderNo)) {
            if (lbxCustomerOrderNosMap[mayItem.customerOrderNo]) {
                lbxCustomerOrderNosMap[mayItem.customerOrderNo].children.push(mayItem);
            } else {
                lbxCustomerOrderNosMap[mayItem.customerOrderNo] = mayItem;
                lbxCustomerOrderNosMap[mayItem.customerOrderNo].children = [lodash.cloneDeep(mayItem)];
            }
        }
    });

    return resultTasks.map(task => {
        if (lbxCustomerOrderNos.has(task.customerOrderNo)) {
            return lbxCustomerOrderNosMap[task.customerOrderNo] || task;
        } else {
            return task;
        }
    });
}

/**
 * 是否为 LBX(菜鸟大件xxx) 运单
 * @param task
 * task (运单任务) 依赖字段
 * - businessMode
 * - taskType
 * - sourceSystem
 * @returns {boolean}
 */
export function isLbxTask(task) {
    return task.businessMode === 'B2B' &&
        ['OUT', 'YS', 'DO'].includes(task.taskType) &&
        task.sourceSystem === 'CAINIAO';
}

/**
 * 压平 LBX 运单
 * @param tasks
 */
export function flatLbxTasks(tasks) {
    const newTasks = [];
    for (const t of tasks) {
        if (Array.isArray(t.children) && t.children.length > 0) {
            newTasks.push(...t.children);
        } else {
            newTasks.push(t);
        }
    }
    return newTasks;
}

/*
 * DONE: 后台返回 "运单任务" 字段补充
 * - businessMode
 * - customerOrderNo
 * - deliveryType
 * - dispatchNo * 有才返回
 * - finalDetailAddr
 * - inOutType
 * - isFull
 * - logisticMode
 * - planOrderFlag *有才返回
 * - scanNum
 * - sourceSystem
 * - taskNo
 * - taskStatus
 * - taskType
 * - warehouseGroup
 * - whCode
 */
